/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, createContext, PropsWithChildren } from 'react';
import {
  Button,
  LoadingButton,
  ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@one-thd/sui-atomic-components';
import { IButtonProps } from '../models/common-models';

interface IModalProps {
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface IModalState {
  title?: string;
  isOpened?: boolean;
  ContentComponent: React.FC<PropsWithChildren<any>> | null;
  componentProps?: PropsWithChildren<any>;
  modalProps?: IModalProps;
  confirmProps?: IButtonProps;
  cancelProps?: IButtonProps;
}

interface IModalProviderProps {
  children: React.ReactNode;
}

interface IModalContextValues {
  isOpened: boolean;
  openModal: (_modalState: IModalState) => void;
  hideModal: () => void;
}

export const ModalContext = createContext<IModalContextValues | null>(null);

const initialState = {
  ContentComponent: null,
  componentProps: {},
  modalProps: {},
  title: '',
  isOpened: false,
  cancelProps: {
    buttonText: ''
  },
  confirmProps: {
    buttonText: 'Yes',
    onClick: () => {}
  }
};

export const ModalProvider = ({ children }: IModalProviderProps) => {
  const [modalState, setModalState] = useState<IModalState>(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const openModal = (_modalState: IModalState) => {
    setModalState((prevState: IModalState) => ({
      ...prevState,
      ..._modalState,
      isOpened: true
    }));
  };

  const hideModal = () => {
    if (isLoading) return;
    setModalState((prevState: IModalState) => ({ ...prevState, isOpened: false }));
    setIsLoading(false);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    if (modalState && modalState.confirmProps && modalState.confirmProps.onClick) {
      await modalState.confirmProps.onClick();
      setIsLoading(false);
    }
  };

  return (
    <ModalContext.Provider
      value={{
        openModal,
        hideModal,
        isOpened: modalState.isOpened || false
      }}
    >
      <Modal open={modalState?.isOpened} onClose={hideModal} {...modalState?.modalProps}>
        <ModalHeader onClose={hideModal}>{modalState?.title}</ModalHeader>
        <ModalBody>
          {modalState.ContentComponent && (
            <modalState.ContentComponent {...modalState.componentProps} />
          )}
        </ModalBody>
        <ModalFooter position="end">
          <ButtonGroup>
            {modalState.cancelProps?.buttonText ? (
              <Button
                data-testid="cancel-modal-button"
                variant="secondary"
                onClick={hideModal}
                {...modalState.cancelProps}
                disabled={isLoading}
              >
                {modalState.cancelProps?.buttonText}
              </Button>
            ) : <></>}
            <LoadingButton
              variant="primary"
              {...modalState.confirmProps}
              loading={isLoading}
              onClick={handleConfirm}
            >
              {modalState.confirmProps?.buttonText || 'Yes'}
            </LoadingButton>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};
